import React, { createContext, useContext, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createDelivery, uploadDeliveryAttachment } from '../../../../../features/deliveries/deliveryActions';
import Toast from '../../../../../components/Toast';
import { getLocalTodayString } from '../../../../../components/CreationDate';
import { deliveryTimeWithOffset, isDeliveryDate, localDeliveryTime, validateDeliveryProductDates } from '../../utils/deliveryDates';

const DeliveryCheckContext = createContext(null);
const emptyProduct = () => ({ id: `${Date.now()}-${Math.random()}`, category: '', description: '', quantity: '', unit: 'kg', batchNumber: '', useByDate: '', bestBefore: '' });
export function DeliveryCheckProvider({ children }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialDate = isDeliveryDate(params.get('date')) ? params.get('date') : getLocalTodayString();
  const [delivery, setDelivery] = useState(() => ({ supplier: '', supplierContact: '', deliveryDateTime: localDeliveryTime(initialDate), deliveryNote: '', poNumber: '', vehicleRegistration: '', driverName: '', driverContact: '', notes: '' }));
  const [products, setProducts] = useState(() => [emptyProduct()]);
  const [attachments, setAttachments] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const savedId = useRef(null);
  const savingRef = useRef(false);
  const user = useSelector(state => state.auth.user?.details);
  const updateDelivery = (field, value) => setDelivery(previous => ({ ...previous, [field]: value }));
  const updateProduct = (id, field, value) => setProducts(previous => previous.map(product => product.id === id ? { ...product, [field]: value } : product));
  const addProduct = () => setProducts(previous => [...previous, emptyProduct()]);
  const removeProduct = id => setProducts(previous => previous.filter(product => product.id !== id));
  const continueToCheck = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setIsSaving(true);
    setError(null);
    try {
      let id = savedId.current;
      if (!id) {
        validateDeliveryProductDates(products);
        const response = await dispatch(createDelivery({ ...delivery, deliveryDateTime: deliveryTimeWithOffset(delivery.deliveryDateTime), products })).unwrap();
        id = response.data.id;
        savedId.current = id;
      }
      for (const file of attachments) {
        await dispatch(uploadDeliveryAttachment({ id, file })).unwrap();
        setAttachments(previous => previous.filter(candidate => candidate !== file));
      }
      navigate(`/checks/delivery-check/${id}`);
    } catch (err) {
      const message = err.message || err || 'Unable to create delivery.';
      setError(message);
      Toast('error', message);
    } finally { savingRef.current = false; setIsSaving(false); }
  };
  return <DeliveryCheckContext.Provider value={{
    delivery, products, updateDelivery, updateProduct, addProduct, removeProduct,
    attachments, setAttachments, continueToCheck, isSaving, error, user,
    detailsSaved: Boolean(savedId.current),
    cancel: () => navigate(`/checks/delivery-check?date=${initialDate}`),
  }}>{children}</DeliveryCheckContext.Provider>;
}
export function useDeliveryCheck() {
  const context = useContext(DeliveryCheckContext);
  if (!context) throw new Error('useDeliveryCheck must be used inside DeliveryCheckProvider');
  return context;
}
